import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { Pair } from '@kodiak-finance/v2-sdk'
import { usePairById } from 'hooks/subgraph/v2'
import { PairState } from 'hooks/useV2Pairs'
import JSBI from 'jsbi'
import { useMemo } from 'react'
import { parseUnits } from 'viem'

export interface V2PositionDetails {
  pair: Pair
  liquidityTokenBalance: CurrencyAmount<Token>
  totalSupply: CurrencyAmount<Token>
  valueUSD: number
}

export interface UseV2PositionByPairResults {
  loading: boolean
  position: V2PositionDetails | undefined
  refetch: () => void
}

export function useV2Pair(pairAddress?: string | null, chainId?: number | null): [PairState, Pair | null] {
  const { data, isLoading } = usePairById(pairAddress ?? '', {
    staleTime: 5 * 60 * 1000,
  })

  return useMemo((): [PairState, Pair | null] => {
    if (isLoading) return [PairState.LOADING, null]
    if (!pairAddress || !chainId) return [PairState.INVALID, null]
    if (!data?.pair) return [PairState.NOT_EXISTS, null]

    try {
      const pairData = data.pair

      const token0 = new Token(
        chainId,
        pairData.token0.id,
        parseInt(pairData.token0.decimals),
        pairData.token0.symbol,
        pairData.token0.name
      )

      const token1 = new Token(
        chainId,
        pairData.token1.id,
        parseInt(pairData.token1.decimals),
        pairData.token1.symbol,
        pairData.token1.name
      )

      const reserve0 = CurrencyAmount.fromRawAmount(
        token0,
        JSBI.BigInt(parseUnits(pairData.reserve0, token0.decimals).toString())
      )

      const reserve1 = CurrencyAmount.fromRawAmount(
        token1,
        JSBI.BigInt(parseUnits(pairData.reserve1, token1.decimals).toString())
      )

      const pair = new Pair(reserve0, reserve1)

      return [PairState.EXISTS, pair]
    } catch (error) {
      console.error('Error processing V2 pair data:', error)
      return [PairState.INVALID, null]
    }
  }, [data, isLoading, pairAddress, chainId])
}
